import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MetaTags } from '../../components/Metatags/Metatags';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { verifyFlutterwavePurchase, verifyPaystackPurchase } from '../../redux/payment/action';
import type { IVerifyFlutterwavePurchaseResult, IVerifyPaystackPurchaseResult } from '../../redux/payment/types';
import { getTokenBalance } from '../../redux/token/actions';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import {
    clearContestTokenPaystackSession,
    isFlutterwaveCallbackParams,
    isPaystackTokenPurchaseVerified,
    readContestTokenCheckoutProvider,
    readContestTokenPaystackContestId,
    readContestTokenPaystackReference,
} from '../../utils/contestTokenPaymentUtils';
import { isFlutterwaveAbandonedReturn } from '../../utils/flutterwaveReturn';

const AUTO_VERIFY_ATTEMPTS = 8;
const MANUAL_VERIFY_ATTEMPTS = 4;
const VERIFY_INTERVAL_MS = 2500;

function pickPaystackReference(searchParams: URLSearchParams): string | null {
    return searchParams.get('reference') ?? searchParams.get('trxref') ?? readContestTokenPaystackReference();
}

function pickFlutterwavePayload(searchParams: URLSearchParams): {
    transaction_id?: string;
    tx_ref?: string;
    reference?: string;
} | null {
    const transaction_id = searchParams.get('transaction_id') ?? undefined;
    const tx_ref =
        searchParams.get('tx_ref') ?? searchParams.get('reference') ?? readContestTokenPaystackReference() ?? undefined;
    if (!transaction_id && !tx_ref) return null;
    return { transaction_id, tx_ref, reference: tx_ref };
}

export default function ContestTokenPaystackCallbackPage() {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const navigate = useNavigate();
    const dispatchPromise = useDispatchPromise();
    const [searchParams] = useSearchParams();
    const verificationRun = useRef(0);
    const [status, setStatus] = useState<'verifying' | 'success' | 'pending' | 'error'>('verifying');
    const [message, setMessage] = useState('Confirming your token purchase…');

    const backToContest = useCallback(() => {
        const contestId = readContestTokenPaystackContestId();
        navigate(contestId ? `/campaign/${contestId}` : ROUTES.FEED, { replace: true });
    }, [navigate]);

    const finishSuccess = useCallback(() => {
        const contestId = readContestTokenPaystackContestId();
        clearContestTokenPaystackSession();
        showToastMessage('Tokens added to your balance', 'success');
        setStatus('success');
        setMessage('Payment confirmed. Redirecting…');
        const target = contestId ? `/campaign/${contestId}` : ROUTES.ESVIEW_CONTEST.replace('/:contestId?', '');
        setTimeout(() => navigate(target, { replace: true }), 1500);
    }, [navigate]);

    const runVerification = useCallback(
        async (maxAttempts: number) => {
            if (isFlutterwaveAbandonedReturn(searchParams)) {
                backToContest();
                return;
            }
            const useFlutterwave =
                isFlutterwaveCallbackParams(searchParams) || readContestTokenCheckoutProvider() === 'flutterwave';

            if (useFlutterwave) {
                const fwPayload = pickFlutterwavePayload(searchParams);
                if (!fwPayload) {
                    setStatus('error');
                    setMessage('Missing payment reference. Return to the contest and try again.');
                    return;
                }

                setStatus('verifying');
                setMessage('Confirming your token purchase…');
                const runId = ++verificationRun.current;
                let lastError: unknown;

                for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                    try {
                        const result = (await dispatchPromise(
                            verifyFlutterwavePurchase(fwPayload),
                        )) as IVerifyFlutterwavePurchaseResult;

                        await dispatchPromise(getTokenBalance({}));
                        if (runId !== verificationRun.current) return;

                        if (isPaystackTokenPurchaseVerified(result)) {
                            finishSuccess();
                            return;
                        }
                        lastError = undefined;
                    } catch (err: unknown) {
                        lastError = err;
                    }

                    if (attempt < maxAttempts) {
                        setMessage(`Payment processing… checking again (${attempt}/${maxAttempts}).`);
                        await new Promise(resolve => window.setTimeout(resolve, VERIFY_INTERVAL_MS));
                        if (runId !== verificationRun.current) return;
                    }
                }

                if (lastError) {
                    const msg =
                        (lastError as { data?: { message?: string } })?.data?.message ??
                        (lastError as Error)?.message ??
                        'Could not verify payment.';
                    setStatus('error');
                    setMessage(msg);
                    return;
                }

                setStatus('pending');
                setMessage('Payment received but still processing. Tap “Check again” to continue checking.');
                return;
            }

            const reference = pickPaystackReference(searchParams);
            if (!reference) {
                setStatus('error');
                setMessage('Missing payment reference. Return to the contest and try again.');
                return;
            }

            setStatus('verifying');
            setMessage('Confirming your token purchase…');
            const runId = ++verificationRun.current;
            let lastError: unknown;

            for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                try {
                    const result = (await dispatchPromise(
                        verifyPaystackPurchase({ reference }),
                    )) as IVerifyPaystackPurchaseResult;

                    // The webhook may credit tokens just after verification, so re-read on every attempt.
                    await dispatchPromise(getTokenBalance({}));
                    if (runId !== verificationRun.current) return;

                    if (isPaystackTokenPurchaseVerified(result)) {
                        finishSuccess();
                        return;
                    }
                    lastError = undefined;
                } catch (err: unknown) {
                    lastError = err;
                }

                if (attempt < maxAttempts) {
                    setMessage(`Payment processing… checking again (${attempt}/${maxAttempts}).`);
                    await new Promise(resolve => window.setTimeout(resolve, VERIFY_INTERVAL_MS));
                    if (runId !== verificationRun.current) return;
                }
            }

            if (lastError) {
                const msg =
                    (lastError as { data?: { message?: string } })?.data?.message ??
                    (lastError as Error)?.message ??
                    'Could not verify payment.';
                setStatus('error');
                setMessage(msg);
                return;
            }

            setStatus('pending');
            setMessage('Payment received but still processing. Tap “Check again” to continue checking.');
        },
        [searchParams, dispatchPromise, finishSuccess, backToContest],
    );

    useEffect(() => {
        void runVerification(AUTO_VERIFY_ATTEMPTS);
        return () => {
            verificationRun.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showActions = status !== 'success';

    return (
        <MetaTags title="Token purchase | Thspian" description="Confirming contest token payment">
            <Box
                sx={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: muiTheme => muiTheme.zIndex.modal,
                    backgroundColor: t.primary_background,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    px: 3,
                    textAlign: 'center',
                }}>
                {status === 'verifying' ? <CircularProgress sx={{ color: t.primary_orange }} /> : null}
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: t.primary_white }}>
                    {status === 'success' ? 'Payment successful' : 'Token purchase'}
                </Typography>
                <Typography sx={{ fontSize: 14, color: t.form_label, maxWidth: 420, lineHeight: 1.5 }}>
                    {message}
                </Typography>
                {showActions && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2, width: 260 }}>
                        <Button
                            variant="contained"
                            disabled={status === 'verifying'}
                            onClick={() => void runVerification(MANUAL_VERIFY_ATTEMPTS)}
                            sx={{
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                bgcolor: t.primary_orange,
                            }}>
                            {status === 'verifying' ? 'Checking…' : 'Check again'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={backToContest}
                            sx={{
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: t.primary_white,
                                borderColor: t.form_label,
                            }}>
                            Back to contest
                        </Button>
                    </Box>
                )}
            </Box>
        </MetaTags>
    );
}
